import { ClaimOfferMinimalDetail } from 'bff';
import { format } from 'date-fns';
import { useTranslations } from 'i11n';
import { Button, Card, Separator, cn } from 'ui';
import { buildUserFullName, currencyFormat } from 'utils';
import { FileListItem, FileListLabel } from './File';
import {
  OfferAgreementDetail,
  OfferAgreementCardActionActor,
  OfferStatusBadge,
  isInactiveOfferStatus,
} from './OfferAgreementCard';
import {
  OfferDetailField,
  OfferDetailGrid,
  OfferDetailValue,
  OfferFileLink,
} from './OfferDetailField';
import { UserIdentity } from './UserDisplay';
import { useNumberFormatter } from '../hooks/useNumberFormatter';
import {
  OFFER_AMOUNT_VARIABLE_NAME,
  OFFER_FEES_VARIABLE_NAME,
  OFFER_INSTALLMENTS_VARIABLE_NAME,
  OFFER_PAYMENT_DAYS_VARIABLE_NAMES,
  OFFER_PUBLIC_REASON_VARIABLE_NAMES,
} from 'piramid-constants';
import { OfferPaymentBreakdownDetail } from './OfferPaymentBreakdownDetail';
import {
  IconEye,
  IconEyeOff,
  IconLock,
  IconLockOpen,
  IconFile,
} from '@tabler/icons-react';

export const ClaimOfferList = ({ children }: { children: React.ReactNode }) => {
  return <div className='grid grid-cols-1 gap-4'>{children}</div>;
};

/**
 * Who is looking at the card. The API already nulls internal fields for the
 * claimant, so this is defence in depth: on 'customer' the internal reasons
 * are never read, let alone rendered.
 */
export type ClaimOfferAudience = 'insurer' | 'customer';

const ReasonLine = ({
  label,
  value,
  internal,
}: {
  /** Omitted for the claimant: a single motive needs no qualifier. */
  label?: string;
  value: string;
  internal?: boolean;
}) => {
  const t = useTranslations('claim.negotiation.offer_card');

  return (
    <div className='flex min-w-0 flex-col gap-0.5'>
      {label && (
        <p className='flex flex-wrap items-center gap-1.5 text-xs font-medium text-stone-700'>
          {label}
          {internal && (
            <span className='rounded border border-stone-200 bg-white px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-stone-500'>
              {t('internal_badge')}
            </span>
          )}
        </p>
      )}
      <p className='text-sm italic text-stone-600'>{value}</p>
    </div>
  );
};

/**
 * Groups the motives of one event. Entries are laid out by the grid itself —
 * no placeholder cells — so a missing motive leaves no hole behind.
 */
const ReasonGroup = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className='flex flex-col gap-1'>
    <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
      {title}
    </p>
    <div className='grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2'>
      {children}
    </div>
  </div>
);

/**
 * Template variables store free text, so the money and percentage ones read
 * as bare digits ("3205000", "10"). They are matched by name, the same
 * convention the wizard and the metrics already rely on.
 */
const useVariableDisplayValue = () => {
  const { formatNumber } = useNumberFormatter();

  return (variable: ClaimOfferMinimalDetail['variable_values'][number]) => {
    const numeric = Number(variable.value);

    if (variable.value.trim() === '' || !Number.isFinite(numeric))
      return variable.value;

    if (variable.name === OFFER_AMOUNT_VARIABLE_NAME)
      return formatNumber(numeric);

    if (variable.name === OFFER_FEES_VARIABLE_NAME) return `${numeric} %`;

    return variable.value;
  };
};

const VariableValue = ({
  variable,
}: {
  variable: ClaimOfferMinimalDetail['variable_values'][number];
}) => {
  const displayValue = useVariableDisplayValue();

  if (variable.type === 'file') {
    const name = variable.value.split('/').pop()?.split('?')[0];

    return <OfferFileLink href={variable.value} name={name} />;
  }

  return (
    <OfferDetailValue title={variable.value}>
      {displayValue(variable)}
    </OfferDetailValue>
  );
};

/** Name, sender, amount and status of an offer: the head of its card. */
export const ClaimOfferSummary = ({
  offer,
  className,
}: {
  offer: Pick<
    ClaimOfferMinimalDetail,
    'name' | 'created_at' | 'offered_by' | 'amount' | 'status'
  >;
  className?: string;
}) => {
  const t = useTranslations('claim.negotiation.offer_card');
  const { formatNumber } = useNumberFormatter();

  const isInactive = isInactiveOfferStatus(offer.status);

  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-4 p-4 bg-stone-50',
        className,
      )}
    >
      <div className='min-w-0'>
        <p className='truncate text-lg font-semibold text-stone-900'>
          {offer.name}
        </p>
        <p className='mt-0.5 text-xs text-muted-foreground'>
          {t('sent_at')}{' '}
          {format(new Date(offer.created_at), 'dd/MM/yyyy HH:mm')}
          {offer.offered_by &&
            ` ${t('sent_by', {
              name: offer.offered_by.first_name ?? 'No disponible',
            })}`}
        </p>
      </div>

      <div className='flex items-center gap-4'>
        {offer.amount !== null && (
          <p
            className={cn(
              'text-lg font-semibold tabular-nums text-stone-900',
              isInactive && 'text-muted-foreground line-through',
            )}
          >
            {formatNumber(offer.amount)}
          </p>
        )}
        <OfferStatusBadge status={offer.status} />
      </div>
    </div>
  );
};

export const ClaimOffer = ({
  audience,
  offer,
  claim,
  onOpenSigningURL,
  withCallToAction = true,
  withSummary = true,
  withAgreement = true,
  actions,
  children,
  classNames,
}: {
  audience: ClaimOfferAudience;
  offer: ClaimOfferMinimalDetail;
  claim: {
    // Only the name is rendered; demanding the full identity forced callers
    // whose payload omits `type` (minimal claim) into unsound casts.
    created_by: Pick<UserIdentity, 'first_name' | 'last_name'>;
  };
  index?: number;
  onOpenSigningURL?: () => void;
  withCallToAction?: boolean;
  /** Off when the caller already shows the summary above the card. */
  withSummary?: boolean;
  /** Off when the caller renders the agreement on its own. */
  withAgreement?: boolean;
  /** Footer slot: each app brings its own call to action and gating. */
  actions?: React.ReactNode;
  children?: React.ReactNode;
  classNames?: {
    footer?: string;
  };
}) => {
  const t = useTranslations('claim.negotiation.offer_card');

  const isInsurer = audience === 'insurer';

  // Only present for company users: the API strips the handler identity from
  // the claimant's payload.
  const senderName = offer.offered_by
    ? buildUserFullName(offer.offered_by).trim()
    : '';

  const isRejectedOnBehalf = !!offer.rejected_on_behalf_by_id;

  // Staff identity is internal: the claimant only learns the company did it.
  const rejectionRecorderName = offer.rejected_on_behalf_by
    ? buildUserFullName(offer.rejected_on_behalf_by).trim()
    : '';

  const offeredReason = isInsurer ? offer.offered_reason : null;
  const canceledReason = isInsurer ? offer.canceled_reason : null;

  const hasReasons =
    !!offer.public_offered_reason ||
    !!offeredReason ||
    !!offer.public_canceled_reason ||
    !!canceledReason ||
    !!offer.rejected_reason;

  // A guided offer stores its terms as columns; the name-matched variables
  // that mirror them for the legacy readers — the `Importe` bridge among them
  // — are not shown twice.
  const typedTerms =
    offer.payment_days !== null
      ? {
          amount: Number(offer.amount ?? 0),
          installments: offer.installments ?? 1,
          fee_percentage:
            offer.fee_percentage !== null ? Number(offer.fee_percentage) : null,
          payment_days: offer.payment_days,
        }
      : null;

  const hiddenVariableNames = [
    ...OFFER_PUBLIC_REASON_VARIABLE_NAMES,
    ...(typedTerms
      ? [
          OFFER_AMOUNT_VARIABLE_NAME,
          OFFER_INSTALLMENTS_VARIABLE_NAME,
          OFFER_FEES_VARIABLE_NAME,
          ...OFFER_PAYMENT_DAYS_VARIABLE_NAMES,
        ]
      : []),
  ];

  const visibleVariables = offer.variable_values.filter(
    (variable) => !hiddenVariableNames.some((name) => name === variable.name),
  );

  const footer =
    actions || children || (offer.status === 'pending' && withCallToAction);

  return (
    <Card className='relative overflow-hidden rounded-2xl'>
      {withSummary && <ClaimOfferSummary offer={offer} />}

      {(typedTerms || visibleVariables.length > 0) && (
        <>
          {withSummary && <Separator />}
          <OfferDetailGrid>
            {typedTerms && <OfferPaymentBreakdownDetail {...typedTerms} />}
            {visibleVariables.map((variable) => (
              <OfferDetailField key={variable.id} label={variable.name}>
                <VariableValue variable={variable} />
              </OfferDetailField>
            ))}
          </OfferDetailGrid>
        </>
      )}

      {hasReasons && (
        <>
          <Separator />
          <div className='flex flex-col gap-4 bg-stone-50/60 p-4'>
            {(offeredReason || offer.public_offered_reason) && (
              <ReasonGroup
                title={isInsurer ? t('offer_reasons') : t('offer_reason')}
              >
                {offeredReason && (
                  <ReasonLine
                    label={t('reason_internal')}
                    value={offeredReason}
                    internal
                  />
                )}
                {offer.public_offered_reason && (
                  <ReasonLine
                    label={isInsurer ? t('reason_public') : undefined}
                    value={offer.public_offered_reason}
                  />
                )}
              </ReasonGroup>
            )}

            {(canceledReason || offer.public_canceled_reason) && (
              <ReasonGroup
                title={isInsurer ? t('canceled_reasons') : t('canceled_reason')}
              >
                {canceledReason && (
                  <ReasonLine
                    label={t('reason_internal')}
                    value={canceledReason}
                    internal
                  />
                )}
                {offer.public_canceled_reason && (
                  <ReasonLine
                    label={isInsurer ? t('reason_public') : undefined}
                    value={offer.public_canceled_reason}
                  />
                )}
              </ReasonGroup>
            )}

            {!!offer.rejected_reason && (
              <ReasonGroup
                title={
                  isRejectedOnBehalf
                    ? t(
                        isInsurer
                          ? 'rejection_on_behalf_title'
                          : 'rejection_on_behalf_title_claimant',
                      )
                    : t('rejection_reason')
                }
              >
                <div className='flex flex-col gap-1 sm:col-span-2'>
                  <ReasonLine value={offer.rejected_reason} />
                  {offer.third_party_user_requested_amount && (
                    <p className='text-xs text-stone-700'>
                      <span className='font-medium'>{t('pretension')}:</span>{' '}
                      {currencyFormat(offer.third_party_user_requested_amount)}
                    </p>
                  )}
                  {isRejectedOnBehalf ? (
                    <p className='text-left text-xs text-muted-foreground'>
                      {rejectionRecorderName
                        ? t('rejected_on_behalf_by', {
                            name: rejectionRecorderName,
                          })
                        : t('rejected_on_behalf_by_company')}{' '}
                      -{' '}
                      {format(new Date(offer.rejected_at!), 'dd/MM/yyyy HH:mm')}
                    </p>
                  ) : (
                    <OfferAgreementCardActionActor
                      date={offer.rejected_at!}
                      first_name={claim.created_by.first_name ?? ''}
                      last_name={claim.created_by.last_name ?? ''}
                    />
                  )}
                </div>
              </ReasonGroup>
            )}
          </div>
        </>
      )}

      {footer && (
        <div
          className={cn(
            'flex w-full items-center justify-end gap-2 px-6 pb-4',
            classNames?.footer,
          )}
        >
          {offer.status === 'pending' && withCallToAction && (
            <a
              href={`/claim/offer/agreement?token=${offer.token}`}
              target='_blank'
            >
              <Button size='sm'>Responder propuesta</Button>
            </a>
          )}
          {actions}
          {children}
        </div>
      )}

      {withAgreement && offer.agreement && (
        <OfferAgreementDetail agreement={offer.agreement} audience={audience} />
      )}
    </Card>
  );
};
